import { Cubit } from "blac-next";
import { ComboBoxAria, Key } from "react-aria";
import { ComboBoxState } from "react-stately";
import { AutocompleteComponentProps } from "./Autocomplete";
import fuzzyMatch from "lib/fuzzyMatch";

export type AutocompleteItem = object & {
  id: string;
  label?: string;
  name?: string;
};

type AutocompleteState = {
  hasFocus: boolean;
};

type AutocompleteProps<T extends AutocompleteItem> = {
  isTouch: boolean;
  componentProps: AutocompleteComponentProps<T>;
  enableInput: boolean;
};

export default class AutocompleteBloc<T extends AutocompleteItem> extends Cubit<
  AutocompleteState,
  AutocompleteProps<T>
> {
  static isolated = true;
  comboBoxState: ComboBoxState<T> | undefined;
  comboBoxProps: ComboBoxAria<T> | undefined;
  refs: {
    inputRef?: React.RefObject<HTMLInputElement | null>;
    popoverRef?: React.RefObject<HTMLDivElement | null>;
    listBoxRef?: React.RefObject<HTMLDivElement | null>;
    buttonRef?: React.RefObject<HTMLButtonElement | null>;
  } = {};
  isTouch: boolean;
  componentProps: AutocompleteComponentProps<T>;
  enableFiltering = true;
  currentValue: string = "";

  constructor(props: AutocompleteProps<T>) {
    super({
      hasFocus: false
    });
    this.isTouch = props.isTouch;
    this.componentProps = props.componentProps;
    // Disable filtering on mobile by not passing defaultFilter
    this.enableFiltering = props.enableInput;
  }

  connectComboBoxState = (comboBoxState: ComboBoxState<T>) => {
    this.comboBoxState = comboBoxState;
  };

  connectComboBoxProps = (comboBoxProps: ComboBoxAria<T>) => {
    this.comboBoxProps = comboBoxProps;
  };

  connectRefs = (refs: typeof this.refs) => {
    this.refs = refs;
  };

  setFocus = (hasFocus: boolean) => {
    this.emit({ ...this.state, hasFocus });
    if (hasFocus) {
      this.enableFiltering = false;
    } else {
      this.enableFiltering = true;
      this.comboBoxState?.close();
    }
  };

  // Method to explicitly open dropdown
  openDropdown = () => {
    if (!this.isTouch && this.comboBoxState && !this.comboBoxState.isOpen) {
      this.comboBoxState.open();
    }
  };

  lastInitialKey: Key | undefined;
  updateSelectedKeyFromDefaultValues = () => {
    // This logic should work for both, setting the initial state.selectedKey
    const initialKey =
      this.componentProps.defaultSelectedKey ??
      this.componentProps.defaultValue;

    if (initialKey && initialKey !== this.lastInitialKey) {
      this.lastInitialKey = initialKey;
      // Find the item in the initial list matching the default value/key
      const defaultItem = this.componentProps.defaultItems.find(
        (item) =>
          item.id === initialKey ||
          item.name === initialKey ||
          item.label === initialKey
      );
      if (defaultItem) {
        // Use the item's actual ID (from the AutocompleteItem definition) as the key
        this.comboBoxState?.setSelectedKey(String(defaultItem.id));
      } else {
        // If not found in defaultItems, maybe it's already in props.items if provided?
        // Or maybe just set the key directly if it's expected to be valid?
        this.comboBoxState?.setSelectedKey(String(initialKey));
      }
    }
  };

  // --- Handlers (Desktop Specific) ---
  handleInputFocus = () => {
    if (this.isTouch) {
      return;
    }
    this.setFocus(true);
    this.refs.inputRef?.current?.select();
  };

  handleInputBlur = () => {
    if (this.isTouch) {
      return;
    }
    this.currentValue = "";
    this.refs.inputRef?.current?.blur();
    this.setFocus(false);
  };

  handleInputChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.currentValue = event.target.value;
  };

  handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    this.comboBoxState?.setSelectedKey(selectedValue || null); // Update state
    this.currentValue = "";
  };

  containsFilter = (item: string): boolean => {
    return fuzzyMatch(this.currentValue, item) !== null;
  };
}
